import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { GALLERY } from "@/lib/site";

const FILTERS = ["All", "Food", "Drinks", "Ambiance"];

export default function Gallery() {
  const [filter, setFilter] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  const items = useMemo(
    () => (filter === "All" ? GALLERY : GALLERY.filter((g) => g.cat === filter)),
    [filter]
  );

  return (
    <section id="gallery" className="relative py-28 sm:py-36" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col sm:flex-row sm:items-end justify-between gap-8 mb-14"
        >
          <div>
            <p className="font-mono-d text-[11px] uppercase tracking-[0.3em] text-terracotta mb-6" data-testid="gallery-overline">
              The Gallery
            </p>
            <h2 className="font-serif-d text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-cream leading-[1.05]" data-testid="gallery-title">
              Under the <span className="italic text-terracotta">spotlight</span>
            </h2>
          </div>
          <div className="flex gap-2.5" data-testid="gallery-filters">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-5 py-2 rounded-full text-[13px] font-medium transition-colors duration-300 ${
                  filter === f
                    ? "bg-terracotta text-obsidian"
                    : "bg-clay-800 text-cream/60 hover:text-cream border border-terracotta/10"
                }`}
                data-testid={`gallery-filter-${f.toLowerCase()}`}
              >
                {f}
              </button>
            ))}
          </div>
        </motion.div>

        <motion.div layout className="grid grid-cols-2 lg:grid-cols-4 auto-rows-[180px] sm:auto-rows-[220px] gap-4" data-testid="gallery-grid">
          <AnimatePresence mode="popLayout">
            {items.map((g, i) => (
              <motion.figure
                layout
                key={g.src}
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.94 }}
                transition={{ duration: 0.45, delay: i * 0.03 }}
                className={`group relative overflow-hidden rounded-2xl cursor-pointer clipped-frame ${
                  i % 5 === 0 ? "col-span-2 row-span-2" : ""
                }`}
                onClick={() => setLightbox(g)}
                data-testid={`gallery-item-${i}`}
              >
                <img
                  src={g.src}
                  alt={g.label}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[1.3s] ease-out group-hover:scale-108 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-obsidian/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-5">
                  <figcaption className="font-mono-d text-[10px] uppercase tracking-[0.2em] text-cream/90">
                    {g.label}
                  </figcaption>
                </div>
              </motion.figure>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-obsidian/95 backdrop-blur-xl flex items-center justify-center p-6"
            onClick={() => setLightbox(null)}
            data-testid="gallery-lightbox"
          >
            <button
              className="absolute top-6 right-6 text-cream/70 hover:text-cream p-2"
              onClick={() => setLightbox(null)}
              data-testid="gallery-lightbox-close"
              aria-label="Close"
            >
              <X className="w-7 h-7" />
            </button>
            <motion.img
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              src={lightbox.src.replace("w=900", "w=1600")}
              alt={lightbox.label}
              className="max-w-full max-h-[82vh] object-contain rounded-xl"
            />
            <p className="absolute bottom-8 font-mono-d text-xs uppercase tracking-[0.25em] text-cream/60">
              {lightbox.label}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
